// Plan 17-07 Task 2 — Signup soft-prompt re-skin (POLISH-13).
// ARCA-82 WS2 — migrated to <Dialog> primitive (scrim/panel/focus-trap live there).
//
// Prototype layout: centered 52px display-word RUNEGAMBIT + tagline + IdP
// CTAs (Apple cyan / Google blue) + guest fallback link.
//
// Existing auth handlers (markSignupSkipped + LoginButtons via useAuth)
// untouched. All raw-px literals replaced with var(--*) tokens.

import { type JSX, useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { markSignupSkipped } from '../../stores/onboarding.js'
import { Btn } from '../components/Btn.js'
import { Dialog } from '../components/Dialog.js'
import { LoginButtons } from '../splash/LoginButtons.js'

interface SignupSoftPromptProps {
  open: boolean
  onClose: () => void
}

export function SignupSoftPrompt({
  open,
  onClose,
}: SignupSoftPromptProps): JSX.Element | null {
  const [showLogin, setShowLogin] = useState(false)
  // Escape (via Dialog/ModalScrim) + Later button both call handleLater so
  // dismissal marks signup as skipped.
  const handleLater = useCallback((): void => {
    markSignupSkipped()
    onClose()
  }, [onClose])
  if (!open) return null

  function handleSignIn(): void {
    setShowLogin(true)
  }

  return (
    // Center-dialog flavor (UI-SPEC §5): scrim + panel + focus trap + Escape
    // dismissal live in <Dialog>/ModalScrim. Escape calls onClose (handleLater)
    // so dismissal marks signup as skipped.
    // rg-screen/signup/modal-shroud dropped: rg-screen adds a sky gradient
    // (wrong on a modal scrim); signup + modal-shroud are undefined in all CSS
    // files (dead classes). The .scrim recipe covers position:fixed + backdrop.
    <Dialog
      onClose={handleLater}
      testId="signup-soft-prompt"
      labelledBy="signup-soft-prompt-title"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-3)',
          padding: 'var(--space-5) var(--space-4)',
        }}
      >
        <h2
          id="signup-soft-prompt-title"
          className="display-word gradient"
          style={{
            fontSize: 'var(--font-size-36)',
            margin: 0,
            maxWidth: '100%',
            textAlign: 'center',
          }}
        >
          <FormattedMessage
            id="onboarding.cta.signup_soft_prompt"
            defaultMessage="Save your progress"
          />
        </h2>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-secondary)',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          <FormattedMessage
            id="onboarding.signup.body"
            defaultMessage="We'll keep your matches and friends across devices. Sign in with Apple or Google."
          />
        </p>
        {showLogin ? (
          <LoginButtons />
        ) : (
          <>
            <Btn kind="primary" size="lg" block onClick={handleSignIn}>
              Sign in
            </Btn>
            <Btn kind="ghost" size="sm" onClick={handleLater}>
              <FormattedMessage
                id="onboarding.cta.signup_later"
                defaultMessage="Later"
              />
            </Btn>
          </>
        )}
      </div>
    </Dialog>
  )
}
